import React from 'react';
import { observer } from 'mobx-react';
import {
    BasicFormWithAlerts,
    FormItem,
    IconTextButton,
    Label,
    notification,
    SelectFormItem,
    SimpleFileUploadItem,
    Spinner,
    TextAreaFieldItem,
    TextFieldItem,
    useForm,
} from 'ui-lib';
import { getErrorMessage, isNullOrEmpty } from 'common-utils';
import { confirmCancel } from '../admin/utils';
import { ERROR_CODES } from '../../../dto/error-codes';
import { useAccountStore, useAuthStore, useKeyStore } from '../../../store';
import { OrganisationSelectItem } from '../components/organisation-select';
import manageKeyCss from './keys.css';
import { ITreeSelectDataSource } from '../../../store/account-store';
import { pspTypesForClient } from './key-utils';
import { KeyEncryptionType, KeyInputType, KeyType, PSP } from 'dto/key-management/key-dto';
import { buildInputRule } from '../components/form-utils';
import { IAccount, AccountType } from 'dto/access-management/account-dto';
import { useSimpleFileUploadProps } from 'ui-lib/components/forms/items/use-simple-file-upload-item';
import { useLoadingStates } from 'hooks/use-loading.hook';

const UPLOAD_KEYS_DESCRIPTION = 'Copy and paste the Key Content provided by your Payment Service Provider';

export interface IUploadKeysComponentProps {
    onCancel: () => void;
    onOk: () => void;
}
function buildTreeDataSourceforKmsAccountsWithValidPspData(
    root: IAccount,
    accountTypes: AccountType[],
    selectableTypes: AccountType[],
    kmsAccounts: string[],
): ITreeSelectDataSource {
    if (!root || (accountTypes !== undefined && !accountTypes.includes(root.accountType))) return undefined;
    const types = selectableTypes ? selectableTypes : Object.values(AccountType);
    const value = { value: root.id, label: root.name, disabled: !types.includes(root.accountType) };
    const subAccounts =
        kmsAccounts && kmsAccounts.length != 0
            ? root.subAccounts.filter(
                  (item) =>
                      item.accountType == 'VAR' ||
                      item.accountType == 'DPU' ||
                      (item.accountType == 'CLIENT' && kmsAccounts.includes(item.uuid)),
              )
            : root.subAccounts;
    const child = subAccounts
        ?.map((sub) =>
            buildTreeDataSourceforKmsAccountsWithValidPspData(sub, accountTypes, selectableTypes, kmsAccounts),
        )
        .filter((sub) => !!sub);
    if (child == undefined || child.length == 0) return { treeDataNode: value };
    return { treeDataNode: value, subTreeDataNode: child };
}
export const UploadKeysComponent = observer((props: IUploadKeysComponentProps) => {
    const accountStore = useAccountStore();
    const { currentUser } = useAuthStore();
    const keyStore = useKeyStore();
    const [kmsAccountWithValidPsp, setKmsAccountWithValidPsp] = React.useState<string[]>([]);
    const { errorMessages, addErrorMessages, clearErrorMessages, loading, withLoading } = useLoadingStates();
    const clientAccountDataSource = React.useMemo<ITreeSelectDataSource>(
        () =>
            buildTreeDataSourceforKmsAccountsWithValidPspData(
                accountStore.accountHierarchy,
                Object.values(AccountType),
                [AccountType.CLIENT],
                kmsAccountWithValidPsp,
            ),
        [kmsAccountWithValidPsp],
    );
    const [selectedClient, setSelectedClient] = React.useState<number>(
        currentUser.accountType == 'CLIENT' && !!clientAccountDataSource
            ? (clientAccountDataSource.treeDataNode.value as number)
            : undefined,
    );
    const [form] = useForm();
    const { pspEncryptionTypeMap, updateSelectedPsp, selectedPsp, supportedKeyTypes } = pspTypesForClient(
        accountStore,
        keyStore,
        selectedClient,
    );
    React.useEffect(() => {
        (async () => {
            const kmsAccounts = await keyStore.getKmsAccountsWithValidPspData();
            setKmsAccountWithValidPsp(kmsAccounts.accountUuids);
        })();
    }, []);

    React.useEffect(() => {
        // Clear the form when the selected psp changes except the common fields
        const keyName = form.getFieldValue('keyName');
        form.resetFields();
        form.setFieldsValue({ 
            keyName: keyName,
            clientAccountId: selectedClient,
            selectedPsp: selectedPsp,
         });
    }, [selectedPsp]);

    const getVarAccountId = (selectedClientId: number) => {
        switch (currentUser.accountType) {
            case AccountType.VAR:
                return currentUser.accountId;
            case AccountType.DPU:
            case AccountType.CLIENT:
                return accountStore.getAccountById(selectedClientId)?.parent?.id;
        }
    };

    const selectedKeyUploadType = React.useMemo(() => {
        switch (selectedPsp) {
            case PSP.LittlePay:
                if (pspEncryptionTypeMap.get(PSP.LittlePay) === KeyEncryptionType.AES128)
                    return KeyInputType.X9_143_KEY_BLOCK;
                return KeyInputType.ENCRYPTED_VALUE;
            default: {
                return KeyInputType.ENCRYPTED_VALUE;
            }
        }
    }, [selectedPsp]);

    const onCancel = () => {
        confirmCancel(() => {
            clearErrorMessages();
            props.onCancel();
        });
    };

    const getUploadKeys = React.useCallback(
        (values: any) => {
            if (selectedKeyUploadType === KeyInputType.X9_143_KEY_BLOCK) {
                return {
                    uploadKeys: [KeyType.BDK, KeyType.LHMAC].map((keyType) => {
                        return {
                            type: keyType,
                            content: values[`${keyType}-${KeyInputType.X9_143_KEY_BLOCK}`],
                            inputType: KeyInputType.X9_143_KEY_BLOCK
                        } 
                    }).filter((key) => !isNullOrEmpty(key.content)),
                };
            }
            const encryptedKey = {
                type: values.keyType,
                content: values.keyContent,
                inputType: KeyInputType.ENCRYPTED_VALUE,
            };
            if (selectedPsp === PSP.ExternalKeyManager) {
                return {
                    uploadKeys: [encryptedKey],
                    files: [...values.exportedKernelConfig, ...values.exportedKeyTable],
                };
            }
            return {
                uploadKeys: [encryptedKey],
            };
        },
        [selectedPsp, selectedKeyUploadType],
    );

    const onSave = async (values): Promise<void> => {
        withLoading(async () => {
            try {
                const { uploadKeys, files } = getUploadKeys(values);
                if (isNullOrEmpty(uploadKeys)) {
                    notification.warning({
                        message: 'Warning',
                        description: `At least one key must be uploaded.`,
                    });
                    return;
                }
                const result = await keyStore.createKeys(
                    {
                        psp: selectedPsp,
                        varAccountId: getVarAccountId(values.clientAccountId),
                        clientAccountId: values.clientAccountId,
                        keyName: values.keyName,
                        uploadKeys,
                    },
                    files,
                );
                addErrorMessages(result.errorMessages);
                if (result.created) {
                    return props.onOk();
                }
            } catch (error) {
                if (error?.message === ERROR_CODES.EXISTS) {
                    addErrorMessages(
                        `You cannot save a new ${supportedKeyTypes.find((keyType) => keyType.name === values.keyType)?.label} for this Client. There are already ${selectedPsp} keys for this Client.`,
                    );
                    return null;
                }
                addErrorMessages(getErrorMessage(error));
            }
        });
    };

    return (
        <Spinner className={manageKeyCss.spinner} spinning={loading}>
            <BasicFormWithAlerts
                cardPros={{ title: 'Upload Keys', bordered: false }}
                form={form}
                className={manageKeyCss.uploadKeys_form}
                errorMessages={errorMessages}
                items={[
                    <FormItem
                        code='description'
                        child={<Label className={manageKeyCss.description} label={UPLOAD_KEYS_DESCRIPTION} />}
                    />,
                    <TextFieldItem labelColSpan={24} code='keyName' label='Key Name' isRequired={true} />,
                    <OrganisationSelectItem
                        labelColSpan={24}
                        code='clientAccountId'
                        label='Client'
                        initialValue={selectedClient}
                        isRequired={true}
                        selectProps={{
                            treeDataSource: clientAccountDataSource,
                            expandAll: true,
                            onChange: setSelectedClient,
                            placeholder:
                                'Clients will be shown in the dropdown only after configuring the PSP(s) in PSP key management by ALM Team',
                        }}
                    />,
                    <SelectFormItem
                        labelColSpan={24}
                        code='selectedPsp'
                        label='PSP'
                        isRequired={true}
                        selectedValue={selectedPsp}
                        onChange={updateSelectedPsp}
                        dataSource={Array.from(pspEncryptionTypeMap.keys()).map((pspType) => ({
                            value: pspType,
                            label: pspType,
                        }))}
                    />,
                    selectedKeyUploadType === KeyInputType.X9_143_KEY_BLOCK ? (
                        <X9143KeyUploadComponent />
                    ) : (
                        <EncryptedKeyUploadComponent
                            psp={selectedPsp}
                            form={form}
                            keyTypes={supportedKeyTypes.map((keyType) => ({ value: keyType.name, label: keyType.label }))}
                        />
                    ),
                    <div className={manageKeyCss.control}>
                        <IconTextButton onClick={onCancel} label='Cancel' />
                        <IconTextButton type='primary' htmlType='submit' label='Save' />
                    </div>,
                ]}
                onSubmit={onSave}
            />
        </Spinner>
    );
});

const X9143KeyUploadComponent = () => {
    const keyBlockRule = buildInputRule(
        '[A-Z0-9]',
        { min: 16 },
        'Accepted characters are uppercase letters and numbers.',
    );
    return (
        <React.Fragment>
            <TextAreaFieldItem
                labelColSpan={24}
                code={`${KeyType.BDK}-${KeyInputType.X9_143_KEY_BLOCK}`}
                label='BDK block'
                textAreaProps={{ autoSize: { minRows: 2, maxRows: 5 } }}
                rule={[keyBlockRule]}
            />
            <TextAreaFieldItem
                labelColSpan={24}
                code={`${KeyType.LHMAC}-${KeyInputType.X9_143_KEY_BLOCK}`}
                label='List HMAC key block'
                textAreaProps={{ autoSize: { minRows: 2, maxRows: 5 } }}
                rule={[keyBlockRule]}
            />
        </React.Fragment>
    );
};

const ExternalKeyUploadComponent = ({ form }) => {
    const exportedKernelConfig = useSimpleFileUploadProps({
        multiple: false,
        uploadButton: { label: 'Click to Upload', type: 'primary' },
        afterFileListChange: (files) => {
            form.setFieldsValue({ exportedKernelConfig: files });
        },
    });

    const exportedKeyTable = useSimpleFileUploadProps({
        multiple: false,
        uploadButton: { label: 'Click to Upload', type: 'primary' },
        afterFileListChange: (files) => {
            form.setFieldsValue({ exportedKeyTable: files });
        },
    });
    return (
        <React.Fragment>
            <TextAreaFieldItem
                labelColSpan={24}
                code='keyContent'
                label='Encrypted Key Table Key (Base64)'
                isRequired={true}
                textAreaProps={{ autoSize: { minRows: 2, maxRows: 5 } }}
            />
            <SimpleFileUploadItem
                labelColSpan={24}
                code='exportedKernelConfig'
                label='Exported Kernel Config'
                isRequired={true}
                initialValue={exportedKernelConfig.files}
                simpleFileUploadProps={exportedKernelConfig.getFileUploadProps()}
            />
            <SimpleFileUploadItem
                labelColSpan={24}
                code='exportedKeyTable'
                label='Exported Key Table'
                isRequired={true}
                simpleFileUploadProps={exportedKeyTable.getFileUploadProps()}
            />
        </React.Fragment>
    );
};
const EncryptedKeyUploadComponent = ({ psp, form, keyTypes }) => {
    const keyContentRule = buildInputRule(
        '[A-Za-z0-9]',
        {},
        'Accepted characters are letters, numbers.',
    );

    return (
        <React.Fragment>
            <SelectFormItem
                labelColSpan={24}
                code='keyType'
                label='Key Type'
                isRequired={true}
                onChange={(value) => form.setFieldsValue({ keyType: value })}
                dataSource={keyTypes}
            />
            {psp === PSP.ExternalKeyManager ? (
                <ExternalKeyUploadComponent form={form} />
            ) : (
                <TextAreaFieldItem
                    labelColSpan={24}
                    code='keyContent'
                    label='Key Content'
                    isRequired={true}
                    textAreaProps={{ autoSize: { minRows: 2, maxRows: 5 } }}
                    rule={psp === PSP.Pulse ? [] : [keyContentRule]}
                />
            )}
        </React.Fragment>
    );
};
